import React, {useMemo} from 'react'

import {CommonConfigurationContext} from '../configuration/CommonConfigurationContext'
import {ConfigurationContext} from '../configuration/ConfigurationContext'
import {buildConfiguration} from '../fields/buildConfiguration'
import {FieldsConfiguration, FieldsForm} from '../fields/types'
import {OrderProcessFormProvider} from '../form/OrderProcessFormProvider'
import {OptionsDto} from '../options/types'
import {Contact} from '../types'

import {buildOrderIntakeAnalyticsData} from './buildOrderIntakeAnalyticsData'
import {OrderIntakeProcessData} from './context/OrderIntakeProcessContext.types'
import {OrderIntakeFeatureToggles} from './featureToggles'
import {buildForm} from './form/buildForm'
import {CreateFormInput} from './form/types'
import {OrderIntakeProcessShell} from './OrderIntakeProcessShell'
import {OrderIntakeType} from './types'

export type OrderIntakeFormCreateCaseProps = {
  orderIntakeType: Extract<OrderIntakeType, 'create'>
}

export type OrderIntakeFormCopyCaseProps = {
  orderIntakeType: Extract<OrderIntakeType, 'copy'>
  // todo: rename
  oldOrder: (input: CreateFormInput<FieldsConfiguration>) => FieldsForm
}

type OrderIntakeProcessProviderProps = React.PropsWithChildren &
  OrderIntakeProcessData & {
    options: OptionsDto
    featureToggles: OrderIntakeFeatureToggles
  } & (OrderIntakeFormCreateCaseProps | OrderIntakeFormCopyCaseProps) & {
    contacts?: {
      orderPlacer?: Contact
      siteContact?: Contact
    }
  }

export const OrderIntakeProcessProvider: React.FC<OrderIntakeProcessProviderProps> = (props) => {
  const {
    children,
    orgUnitCountryId: country,
    options,
    featureToggles,
    orgUnitId,
    shippingAddress,
    contacts,
    customer,
    orderIntakeType,
    orderId
  } = props

  const inputProps = useMemo(
    () => ({
      confirmationPinRequired: !!customer?.confirmationPinRequired,
      enableLoadSpacingAutoAdjustment: true
    }),
    [customer?.confirmationPinRequired]
  )

  const configuration = useMemo(
    () =>
      buildConfiguration({
        options,
        inputProps,
        featureToggles: {
          enableSlotManagement: featureToggles.enableSlotManagementForRmcOrderIntake,
          enableConfirmationPin: featureToggles.enableConfirmationPin,
          enableContractSelection: featureToggles.enableOrderIntakeContractSelection
        }
      }),
    [options, inputProps, featureToggles]
  )

  const form = useMemo(
    () =>
      props.orderIntakeType === 'create'
        ? buildForm({configuration, inputProps}, contacts)
        : props.oldOrder({configuration, inputProps}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configuration, inputProps, props] // should that be updated when configuration changed?
  )

  const analyticData = useMemo(
    () => buildOrderIntakeAnalyticsData(props.orderIntakeType, form, props),
    [form, props]
  )

  const orderIntakeProcessData = useMemo(
    () => ({
      processData: {
        orgUnitId,
        orgUnitCountryId: country,
        shippingAddress,
        customer,
        orderId,
        orderIntakeType
      }
    }),
    [orgUnitId, country, shippingAddress, customer, orderId, orderIntakeType]
  )

  const slotManagementConfig = useMemo(
    () => ({
      isFeatureEnabled: featureToggles.enableSlotManagementForRmcOrderIntake,
      orgUnitId,
      siteNumber: shippingAddress.siteNumber,
      orderId
    }),
    [
      featureToggles.enableSlotManagementForRmcOrderIntake,
      orgUnitId,
      shippingAddress.siteNumber,
      orderId
    ]
  )

  return (
    <OrderIntakeProcessShell
      analyticsData={analyticData}
      orderIntakeProcessData={orderIntakeProcessData}
      slotManagementConfig={slotManagementConfig}
      renderConfigurationProviders={(content) => (
        <CommonConfigurationContext.Provider value={configuration}>
          <ConfigurationContext.Provider value={configuration}>
            {content}
          </ConfigurationContext.Provider>
        </CommonConfigurationContext.Provider>
      )}
      renderFormProvider={(content) => (
        <OrderProcessFormProvider country={country} defaultValues={form}>
          {content}
        </OrderProcessFormProvider>
      )}
    >
      {children}
    </OrderIntakeProcessShell>
  )
}
